import { useCallback } from 'react';
import { brandIsPh } from '@/shared/config/brand-by-target.mjs';
import { formatMoney } from '@/shared/libs/format-bonus-offer';
import { formatCurrency, getCurrencySymbol } from '@/shared/libs/format-currency';
import { useUserCurrency } from '@/shared/providers/user-provider/use-user';
import { getThemeBrand } from './useBrandTheme';
import { useCurrencySymbol } from './useCurrencySymbol';

interface FormatAmountOptions {
  mode?: 'brand' | 'plain';
  guest?: boolean;
}

interface FormatAmountOverrides {
  currency?: string | null;
  currencySymbol?: string;
  settings?: Parameters<typeof formatCurrency>[2];
}

const { guestCode } = getThemeBrand().currency;

export const useFormatAmount = ({ mode = 'brand', guest = false }: FormatAmountOptions = {}) => {
  const userCurrency = useUserCurrency();
  const ownCurrency = guest ? userCurrency || guestCode : userCurrency;
  const ownSymbol = useCurrencySymbol({ guest });

  return useCallback(
    (amount: number | string, { currency = ownCurrency, currencySymbol, settings }: FormatAmountOverrides = {}) => {
      const isOwnCurrency = !currency || currency === ownCurrency;
      const symbol = currencySymbol ?? (isOwnCurrency ? ownSymbol : getCurrencySymbol(currency));

      if (mode === 'brand' && brandIsPh) return formatMoney(amount, symbol);
      return formatCurrency(amount, { currency, currencySymbol: symbol }, settings) ?? '';
    },
    [mode, ownCurrency, ownSymbol]
  );
};
